import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../libs/firebaseConfig";

export const DEFAULT_ACCESS_CONTROL_SETTINGS = {
  enabled: false,
  enforceDeviceSlots: true,
  singleSessionEnabled: false,

  // ✅ เปิดทดสอบเฉพาะ admin ก่อน
  adminOnlyTestMode: true,

  allowDesktop: true,
  allowTablet: true,
  allowMobile: true,

  desktopWindowDays: 14,
  tabletWindowDays: 14,
  mobileWindowDays: 14,

  blockWhenMismatchWithinWindow: true,
  requireConfirmToReplaceAfterWindow: true,

  showWatermark: true,
  watermarkShowEmail: true,
  watermarkShowPhoneMask: false,
  watermarkShowUidTail: true,

  enableDeviceMonitoring: false,
  enableDeviceMonitoringAdminOnly: true,
  enableDeviceWarningModal: true,
  deviceMonitorIntervalHours: 6,
  deviceWarningThreshold: 2,
  deviceRiskThreshold: 3,

  enableCourseActiveDeviceLock: false,
  enableCourseActiveDeviceLockAdminOnly: true,
  courseActiveHeartbeatSeconds: 45,
  courseActiveTimeoutSeconds: 120,
};

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + Number(days || 0));
  return d;
}

function toDateSafe(value) {
  try {
    if (!value) return null;
    if (value?.toDate) return value.toDate();
    if (value instanceof Date) return value;
    if (typeof value === "string" || typeof value === "number") {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) return d;
    }
  } catch {}
  return null;
}

function detectDeviceType() {
  if (typeof window === "undefined") return "desktop";

  const ua = String(navigator.userAgent || "").toLowerCase();
  const width = Number(window.innerWidth || window.screen?.width || 0);
  const height = Number(window.innerHeight || window.screen?.height || 0);
  const shortSide = Math.min(width, height);
  const longSide = Math.max(width, height);
  const touchPoints = Number(navigator.maxTouchPoints || 0);

  const isTabletUA =
    /ipad|tablet/.test(ua) ||
    (/android/.test(ua) && !/mobile/.test(ua));

  const isMobileUA =
    /iphone|ipod|mobile|android.*mobile|windows phone/.test(ua);

  if (isTabletUA) return "tablet";
  if (isMobileUA) return "mobile";

  // ✅ iPadOS บางรุ่นรายงาน UA เหมือน Mac แต่มี touchPoints
  // ใช้ shortSide/longSide เพื่อไม่ให้พลิกจอแล้ว slot เปลี่ยน
  if (touchPoints > 0 && shortSide >= 600 && longSide <= 1400) {
    return "tablet";
  }

  return "desktop";
}

function maskEmail(email) {
  const v = String(email || "").trim();
  if (!v || !v.includes("@")) return "";
  const [name, domain] = v.split("@");
  if (!name || !domain) return v;
  if (name.length <= 2) return `${name[0] || "*"}***@${domain}`;
  return `${name.slice(0, 2)}***@${domain}`;
}

function maskPhone(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length <= 4) return `***${digits}`;
  return `${digits.slice(0, 3)}***${digits.slice(-2)}`;
}

function buildWatermarkText({ user, userDoc, settings }) {
  if (!settings?.showWatermark) return "";

  const parts = [];

  const email = String(userDoc?.email || user?.email || "").trim();
  const phone =
    String(userDoc?.enrollProfile?.phoneNumber || "").trim() ||
    String(userDoc?.phoneNumber || "").trim();

  if (settings?.watermarkShowEmail && email) {
    parts.push(maskEmail(email));
  }

  if (settings?.watermarkShowPhoneMask && phone) {
    parts.push(maskPhone(phone));
  }

  const displayName = String(userDoc?.displayName || user?.displayName || "").trim();
  if (!parts.length && displayName) {
    parts.push(displayName);
  }

  if (settings?.watermarkShowUidTail && user?.uid) {
    parts.push(`ID:${String(user.uid).slice(-6)}`);
  }

  return parts.join(" • ");
}

async function sha256(text) {
  try {
    if (
      typeof window !== "undefined" &&
      window.crypto &&
      window.crypto.subtle &&
      typeof TextEncoder !== "undefined"
    ) {
      const data = new TextEncoder().encode(String(text || ""));
      const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch {}

  let hash = 0;
  const str = String(text || "");
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return `fallback_${Math.abs(hash)}_${str.length}`;
}

export async function buildDeviceContext() {
  const slot = detectDeviceType();

  const userAgent = String(navigator.userAgent || "");
  const platform = String(navigator.platform || "");
  const language = String(navigator.language || "");
  const timezone =
    Intl.DateTimeFormat?.().resolvedOptions?.().timeZone || "";
  // const screenSize = `${window.screen?.width || 0}x${window.screen?.height || 0}`;
  const touchPoints = Number(navigator.maxTouchPoints || 0);

  const screenW = Math.round((window.screen?.width || 0) / 100);
  const screenH = Math.round((window.screen?.height || 0) / 100);
  const screenMin = Math.min(
    window.screen?.width || 0,
    window.screen?.height || 0
  );

  const screenMax = Math.max(
    window.screen?.width || 0,
    window.screen?.height || 0
  );

  const screenSize = `${screenMin}x${screenMax}`;

  const raw = [
    slot,
    platform,
    timezone,
    screenMin,
    screenMax,
  ].join("|");

  const signature = await sha256(raw);

  return {
    slot,
    signature,
    label: `${slot} • ${platform || "unknown"} • ${screenSize} • ${timezone || "unknown"}`,
    meta: {
      userAgent,
      platform,
      language,
      timezone,
      screenSize,
      touchPoints,
    },
  };
}

export async function getAccessControlSettings() {
  try {
    const snap = await getDoc(doc(db, "systemSettings", "accessControl"));
    if (!snap.exists()) return { ...DEFAULT_ACCESS_CONTROL_SETTINGS };

    return {
      ...DEFAULT_ACCESS_CONTROL_SETTINGS,
      ...snap.data(),
    };
  } catch {
    return { ...DEFAULT_ACCESS_CONTROL_SETTINGS };
  }
}

async function writeAccessLog(uid, payload) {
  try {
    await addDoc(collection(db, "users", uid, "accessLogs"), {
      ...payload,
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    console.warn("writeAccessLog failed:", e?.message);
  }
}

function getWindowDaysBySlot(settings, slot) {
  if (slot === "tablet") return Number(settings?.tabletWindowDays || 14);
  if (slot === "mobile") return Number(settings?.mobileWindowDays || 14);
  return Number(settings?.desktopWindowDays || 14);
}

function isSlotAllowed(settings, slot) {
  if (slot === "tablet") return settings?.allowTablet !== false;
  if (slot === "mobile") return settings?.allowMobile !== false;
  return settings?.allowDesktop !== false;
}

export async function runCourseAccessCheck({
  user,
  userDoc,
  courseId,
  previewMode = false,
}) {
  const settings = await getAccessControlSettings();

  const override = userDoc?.accessControlOverride || {};
  const hasStrictOverride = override?.enforceDeviceSlots === true;

  const effectiveSettings = {
    ...settings,

    enabled: hasStrictOverride ? true : settings.enabled,

    enforceDeviceSlots: hasStrictOverride
      ? true
      : settings.enforceDeviceSlots,

    desktopWindowDays: hasStrictOverride
      ? Number(override.deviceLockDays || 14)
      : settings.desktopWindowDays,

    tabletWindowDays: hasStrictOverride
      ? Number(override.deviceLockDays || 14)
      : settings.tabletWindowDays,

    mobileWindowDays: hasStrictOverride
      ? Number(override.deviceLockDays || 14)
      : settings.mobileWindowDays,

    // ✅ ถ้าเป็น strict รายบุคคล ไม่ให้ adminOnlyTestMode มายกเว้น user ทั่วไป
    adminOnlyTestMode: hasStrictOverride
      ? false
      : settings.adminOnlyTestMode,
  };

  if (previewMode) {
    return {
      ok: true,
      status: "allowed",
      message: "",
      settings: effectiveSettings,
      watermarkText: buildWatermarkText({
        user,
        userDoc,
        settings: effectiveSettings,
      }),
    };
  }

  if (!user?.uid) {
    return {
      ok: false,
      status: "denied",
      message: "กรุณาเข้าสู่ระบบก่อนเข้าเรียน",
      settings: effectiveSettings,
      watermarkText: "",
    };
  }

  if (!effectiveSettings?.enabled || !effectiveSettings?.enforceDeviceSlots) {
    return {
      ok: true,
      status: "allowed",
      message: "",
      settings: effectiveSettings,
      watermarkText: buildWatermarkText({
        user,
        userDoc,
        settings: effectiveSettings,
      }),
    };
  }

  // ✅ โหมดทดสอบเฉพาะ admin ใช้เฉพาะกรณี global lock
  // ถ้า user มี strict override รายบุคคล จะไม่ถูกยกเว้น
  if (effectiveSettings?.adminOnlyTestMode === true) {
    const role = String(userDoc?.role || "").trim();

    if (role !== "admin") {
      return {
        ok: true,
        status: "allowed",
        message: "",
        settings: effectiveSettings,
        watermarkText: buildWatermarkText({
          user,
          userDoc,
          settings: effectiveSettings,
        }),
      };
    }
  }

  const device = await buildDeviceContext();
  const uid = user.uid;
  const now = new Date();

  const labelMap = {
    desktop: "คอมพิวเตอร์",
    mobile: "มือถือ",
    tablet: "แท็บเล็ต",
  };

  const deviceLabel = labelMap[device.slot] || device.slot;

  if (!isSlotAllowed(effectiveSettings, device.slot)) {
    const message = `ระบบยังไม่อนุญาตให้เข้าเรียนผ่านอุปกรณ์ประเภท ${deviceLabel}`;

    await writeAccessLog(uid, {
      courseId,
      slot: device.slot,
      action: "course_access_check",
      result: "denied",
      message,
      signature: device.signature,
    });

    return {
      ok: false,
      status: "denied",
      message,
      settings: effectiveSettings,
      device,
      watermarkText: "",
    };
  }

  const slotRef = doc(db, "users", uid, "accessDevices", device.slot);
  const slotSnap = await getDoc(slotRef);

  if (!slotSnap.exists()) {
    const days = getWindowDaysBySlot(effectiveSettings, device.slot);

    await setDoc(
      slotRef,
      {
        slot: device.slot,
        currentSignature: device.signature,
        currentLabel: device.label,
        firstBoundAt: Timestamp.fromDate(now),
        lockedUntil: Timestamp.fromDate(addDays(now, days)),
        lastSeenAt: serverTimestamp(),

        previousSignature: "",
        previousLabel: "",

        lastUserAgent: device.meta.userAgent,
        lastPlatform: device.meta.platform,
        lastTimezone: device.meta.timezone,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    await writeAccessLog(uid, {
      courseId,
      slot: device.slot,
      action: "course_access_check",
      result: "allowed",
      message: "bind_first_device",
      signature: device.signature,
    });

    return {
      ok: true,
      status: "allowed",
      message: "",
      settings: effectiveSettings,
      device,
      watermarkText: buildWatermarkText({
        user,
        userDoc,
        settings: effectiveSettings,
      }),
    };
  }

  const current = slotSnap.data() || {};
  const currentSignature = String(current?.currentSignature || "");

  if (currentSignature && currentSignature === device.signature) {
    await setDoc(
      slotRef,
      {
        lastSeenAt: serverTimestamp(),
        currentLabel: device.label,
        lastUserAgent: device.meta.userAgent,
        lastPlatform: device.meta.platform,
        lastTimezone: device.meta.timezone,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    await writeAccessLog(uid, {
      courseId,
      slot: device.slot,
      action: "course_access_check",
      result: "allowed",
      message: "same_device",
      signature: device.signature,
    });

    return {
      ok: true,
      status: "allowed",
      message: "",
      settings: effectiveSettings,
      device,
      slotDoc: current,
      watermarkText: buildWatermarkText({
        user,
        userDoc,
        settings: effectiveSettings,
      }),
    };
  }

  const lockedUntil = toDateSafe(current?.lockedUntil);

  if (
    effectiveSettings?.blockWhenMismatchWithinWindow !== false &&
    lockedUntil &&
    now < lockedUntil
  ) {
    let message =
      `ตรวจพบ${deviceLabel}เครื่องใหม่ และยังไม่สามารถเปลี่ยนได้จนกว่าจะครบกำหนด`;

    if (lockedUntil) {
      const thaiDateTime = lockedUntil.toLocaleString("th-TH", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

      message =
        `ตรวจพบ${deviceLabel}เครื่องใหม่ และยังไม่สามารถเปลี่ยนอุปกรณ์ได้ในขณะนี้\n\n` +
        `คุณสามารถเปลี่ยนอุปกรณ์ได้อีกครั้งตั้งแต่วันที่ ${thaiDateTime}`;
    }

    await writeAccessLog(uid, {
      courseId,
      slot: device.slot,
      action: "course_access_check",
      result: "denied",
      message,
      signature: device.signature,
    });

    return {
      ok: false,
      status: "denied",
      message,
      settings: effectiveSettings,
      device,
      slotDoc: current,
      canReplaceAt: lockedUntil,
      watermarkText: "",
    };
  }

  if (effectiveSettings?.requireConfirmToReplaceAfterWindow !== false) {
    const message = `ตรวจพบ${deviceLabel}เครื่องใหม่ คุณต้องยืนยันการเปลี่ยนอุปกรณ์ก่อน`;

    await writeAccessLog(uid, {
      courseId,
      slot: device.slot,
      action: "course_access_check",
      result: "confirm_replace",
      message,
      signature: device.signature,
    });

    return {
      ok: false,
      status: "confirm_replace",
      message,
      settings: effectiveSettings,
      device,
      slotDoc: current,
      watermarkText: "",
    };
  }

  return confirmReplaceAccessDevice({
    user,
    userDoc,
    courseId,
    settings: effectiveSettings,
    device,
  });
}

export async function confirmReplaceAccessDevice({
  user,
  userDoc,
  courseId,
  settings,
  device,
}) {
  const resolvedSettings = settings || (await getAccessControlSettings());
  const resolvedDevice = device || (await buildDeviceContext());

  const uid = user?.uid;
  if (!uid) {
    return {
      ok: false,
      status: "denied",
      message: "กรุณาเข้าสู่ระบบก่อน",
      settings: resolvedSettings,
      watermarkText: "",
    };
  }

  const slotRef = doc(db, "users", uid, "accessDevices", resolvedDevice.slot);
  const slotSnap = await getDoc(slotRef);
  const current = slotSnap.exists() ? slotSnap.data() || {} : {};

  const days = getWindowDaysBySlot(resolvedSettings, resolvedDevice.slot);

  await setDoc(
    slotRef,
    {
      slot: resolvedDevice.slot,

      previousSignature: current?.currentSignature || "",
      previousLabel: current?.currentLabel || "",

      currentSignature: resolvedDevice.signature,
      currentLabel: resolvedDevice.label,

      firstBoundAt: current?.firstBoundAt || serverTimestamp(),
      lockedUntil: Timestamp.fromDate(addDays(new Date(), days)),
      lastSeenAt: serverTimestamp(),

      lastUserAgent: resolvedDevice.meta.userAgent,
      lastPlatform: resolvedDevice.meta.platform,
      lastTimezone: resolvedDevice.meta.timezone,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  await writeAccessLog(uid, {
    courseId,
    slot: resolvedDevice.slot,
    action: "course_access_replace_confirmed",
    result: "allowed",
    message: "device_replaced",
    signature: resolvedDevice.signature,
  });

  return {
    ok: true,
    status: "allowed",
    message: "",
    settings: resolvedSettings,
    device: resolvedDevice,
    watermarkText: buildWatermarkText({
      user,
      userDoc,
      settings: resolvedSettings,
    }),
  };
}